import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Truck, Package, Tag, Loader2, Lock, Gift } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const FULFILL = [
    { key: "pickup", label: "Pickup", icon: Store, note: "Free · Ready in 2 hrs" },
    { key: "delivery", label: "Local Delivery", icon: Truck, note: "Same-day nearby" },
    { key: "shipping", label: "Shipping", icon: Package, note: "2–3 day chilled" },
];

function nextDates(n = 5) {
    return Array.from({ length: n }).map((_, i) => {
        const d = new Date();
        d.setDate(d.getDate() + i + 1);
        return { value: d.toISOString().slice(0, 10), label: d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) };
    });
}

export default function Checkout() {
    const { cart, count, setGiftWrap, setGiftMessage, clear } = useCart();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [name, setName] = useState(user?.name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [fulfillment, setFulfillment] = useState(() => {
        try { return localStorage.getItem("sp_fulfillment") || "shipping"; } catch { return "shipping"; }
    });
    const dates = useMemo(() => nextDates(), []);
    const [date, setDate] = useState(dates[0]?.value);
    const [code, setCode] = useState("");
    const [appliedCode, setAppliedCode] = useState("");
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paying, setPaying] = useState(false);

    useEffect(() => {
        if (user) { setName(user.name || ""); setEmail(user.email || ""); }
    }, [user]);

    const payload = useCallback((discountCode) => ({
        items: cart.items.map((i) => ({ product_id: i.product_id, size_key: i.size_key, quantity: i.quantity, customization: i.customization || null })),
        boxes: cart.boxes.map((b) => ({ box_key: b.box_key, size_key: b.size_key || "cup12", flavors: b.flavors, quantity: b.quantity })),
        gift_wrap: cart.giftWrap,
        gift_message: cart.giftMessage || null,
        fulfillment,
        fulfillment_date: date,
        discount_code: discountCode || null,
        email: email || null,
        name: name || null,
        origin_url: window.location.origin,
    }), [cart, fulfillment, date, email, name]);

    const refreshQuote = useCallback(async (discountCode) => {
        if (count === 0) return;
        setLoading(true);
        try {
            const { data } = await api.post("/checkout/quote", payload(discountCode));
            setQuote(data);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setLoading(false);
        }
    }, [count, payload]);

    useEffect(() => { refreshQuote(appliedCode); /* eslint-disable-next-line */ }, [fulfillment, cart, appliedCode]);

    const applyCode = async () => {
        if (!code.trim()) return;
        try {
            await api.post("/checkout/quote", payload(code.trim()));
            setAppliedCode(code.trim().toUpperCase());
            toast.success(`Code ${code.trim().toUpperCase()} applied`);
        } catch (e) {
            toast.error(apiError(e, "Invalid discount code"));
        }
    };

    const pay = async () => {
        if (!email || !name) { toast.error("Please add your name and email"); return; }
        setPaying(true);
        try {
            const { data } = await api.post("/checkout/create", payload(appliedCode));
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(apiError(e, "Could not start checkout"));
            setPaying(false);
        }
    };

    if (count === 0) {
        return (
            <div className="mx-auto max-w-2xl px-5 py-24 text-center">
                <h1 className="font-serif text-4xl text-roast">Your cart is empty</h1>
                <p className="mt-3 text-mocha">Add a few sweet things before checking out.</p>
                <Button onClick={() => navigate("/shop")} className="mt-6 rounded-full bg-banana px-6 text-roast hover:bg-banana-hover">Shop Flavors</Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-6xl px-5 py-12 lg:px-8">
            <h1 className="font-serif text-4xl font-semibold text-roast">Checkout</h1>
            <div className="mt-8 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
                {/* Left form */}
                <div className="space-y-8">
                    <section>
                        <h2 className="font-serif text-2xl text-roast">Contact</h2>
                        <p className="mb-4 text-sm text-mocha">{user ? "Signed in — reorder anytime from your account." : "Checking out as guest. Sign in for faster checkout & order history."}</p>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Input data-testid="checkout-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name *" className="h-12 rounded-xl border-caramel/15 bg-white focus-visible:ring-caramel" />
                            <Input data-testid="checkout-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address *" className="h-12 rounded-xl border-caramel/15 bg-white focus-visible:ring-caramel" />
                        </div>
                        <p className="mt-2 text-xs text-mocha">Name and email are required to place your order.</p>
                    </section>

                    <section>
                        <h2 className="font-serif text-2xl text-roast">Fulfillment</h2>
                        <div className="mt-4 grid gap-3 sm:grid-cols-3">
                            {FULFILL.map((f) => (
                                <button key={f.key} onClick={() => { setFulfillment(f.key); try { localStorage.setItem("sp_fulfillment", f.key); } catch {} }} data-testid={`fulfill-${f.key}`}
                                    className={`rounded-2xl border p-4 text-left transition-all ${fulfillment === f.key ? "border-caramel bg-banana/15" : "border-caramel/15 bg-white hover:border-caramel/40"}`}>
                                    <f.icon size={22} className="text-caramel" />
                                    <p className="mt-2 font-medium text-roast">{f.label}</p>
                                    <p className="text-xs text-mocha">{f.note}</p>
                                </button>
                            ))}
                        </div>
                        <div className="mt-4">
                            <label className="mb-2 block text-sm font-medium text-roast">Choose a {fulfillment} date</label>
                            <div className="flex flex-wrap gap-2">
                                {dates.map((d) => (
                                    <button key={d.value} onClick={() => setDate(d.value)} data-testid={`date-${d.value}`}
                                        className={`rounded-full px-4 py-2 text-sm transition-colors ${date === d.value ? "bg-roast text-cream-50" : "bg-cream-200 text-roast hover:bg-cream-300"}`}>
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section>
                        <h2 className="font-serif text-2xl text-roast">Gift options</h2>
                        <label className="mt-3 flex cursor-pointer items-center gap-3 rounded-2xl border border-caramel/15 bg-white p-4">
                            <input type="checkbox" checked={cart.giftWrap} onChange={(e) => setGiftWrap(e.target.checked)} data-testid="checkout-giftwrap" className="h-5 w-5 accent-caramel" />
                            <span className="flex items-center gap-2 text-roast"><Gift size={18} className="text-caramel" /> Add signature gift packaging <span className="text-mocha">+$6</span></span>
                        </label>
                        <Textarea data-testid="checkout-giftmsg" value={cart.giftMessage} onChange={(e) => setGiftMessage(e.target.value)} placeholder="Add a personalized gift message (optional)" className="mt-3 rounded-xl border-caramel/15 bg-white focus-visible:ring-caramel" />
                    </section>
                </div>

                {/* Right summary */}
                <div className="lg:sticky lg:top-24 lg:self-start">
                    <div className="rounded-3xl border border-caramel/15 bg-white p-6 shadow-warm">
                        <h2 className="font-serif text-2xl text-roast">Order Summary</h2>
                        <div className="mt-4 max-h-64 space-y-3 overflow-y-auto">
                            {quote?.items?.map((it, i) => (
                                <div key={i} className="flex items-center gap-3">
                                    {it.image ? <img src={it.image} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <div className="grid h-12 w-12 place-items-center rounded-lg bg-cream-200 text-caramel"><Gift size={18} /></div>}
                                    <div className="min-w-0 flex-1"><p className="truncate text-sm font-medium text-roast">{it.name}</p><p className="text-xs text-mocha">Qty {it.quantity}</p></div>
                                    <span className="text-sm font-medium text-roast">${it.line_total.toFixed(2)}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-5 flex gap-2">
                            <div className="relative flex-1">
                                <Tag size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mocha" />
                                <Input data-testid="discount-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Discount code" className="h-11 rounded-full border-caramel/15 bg-cream-50 pl-10 focus-visible:ring-caramel" />
                            </div>
                            <Button onClick={applyCode} data-testid="apply-discount-btn" variant="outline" className="h-11 rounded-full border-caramel/30 px-5 text-caramel hover:bg-cream-200">Apply</Button>
                        </div>
                        <p className="mt-2 text-xs text-mocha">Try <span className="font-medium text-caramel">WELCOME10</span> for 10% off.</p>

                        <div className="mt-5 space-y-2 border-t border-caramel/10 pt-4 text-sm">
                            <div className="flex justify-between text-mocha"><span>Subtotal</span><span data-testid="sum-subtotal">${(quote?.subtotal || 0).toFixed(2)}</span></div>
                            {quote?.discount > 0 && <div className="flex justify-between text-caramel"><span>Discount</span><span data-testid="sum-discount">−${quote.discount.toFixed(2)}</span></div>}
                            <div className="flex justify-between text-mocha"><span>{fulfillment === "pickup" ? "Pickup" : "Shipping"}</span><span data-testid="sum-shipping">{quote?.shipping ? `$${quote.shipping.toFixed(2)}` : "Free"}</span></div>
                            <div className="flex justify-between border-t border-caramel/10 pt-3 text-lg font-semibold text-roast"><span>Total</span><span data-testid="sum-total">${(quote?.total || 0).toFixed(2)}</span></div>
                        </div>

                        <Button onClick={pay} disabled={paying || loading} data-testid="pay-btn" className="mt-5 h-12 w-full rounded-full bg-caramel text-base text-cream-50 hover:bg-caramel-dark">
                            {paying ? <><Loader2 size={18} className="mr-2 animate-spin" /> Redirecting…</> : <><Lock size={16} className="mr-2" /> Pay securely</>}
                        </Button>
                        <p className="mt-3 text-center text-xs text-mocha">Cards · Apple Pay · Google Pay via Stripe. Test card 4242 4242 4242 4242.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
